import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, X as XIcon, BookOpen, Users2, PowerOff, Power, Sparkles } from "lucide-react";
import { toast } from "sonner";

const GROUPS_ORDER = ["KG", "NURSERY", "BASIC_1_2", "BASIC_3_5", "JSS", "CRECHE", "ISLAMIYYA", "TAHFIZ"];
const GROUP_TONE = {
  KG: "bg-pink-100 text-pink-800 border-pink-200",
  NURSERY: "bg-purple-100 text-purple-800 border-purple-200",
  BASIC_1_2: "bg-sky-100 text-sky-800 border-sky-200",
  BASIC_3_5: "bg-blue-100 text-blue-800 border-blue-200",
  JSS: "bg-[#1B2C5A] text-white border-[#1B2C5A]",
  CRECHE: "bg-rose-100 text-rose-800 border-rose-200",
  ISLAMIYYA: "bg-emerald-100 text-emerald-800 border-emerald-200",
  TAHFIZ: "bg-amber-100 text-amber-800 border-amber-200",
};

const emptyForm = { name: "", code: "", class_group: "KG", is_active: true, description: "" };

const Subjects = () => {
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const canManage =
    hasEffectiveCapability("subject.manage");

  const [subjects, setSubjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [showActive, setShowActive] = useState(false);

  const [openDlg, setOpenDlg] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  // Assignment dialog
  const [asnOpen, setAsnOpen] = useState(false);
  const [asnSubject, setAsnSubject] = useState(null);
  const [asnRows, setAsnRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [asnTerm, setAsnTerm] = useState("first");

  const load = async () => {
    try {
      const params = {};
      if (group !== "all") params.class_group = group;
      if (showActive) params.active_only = true;
      if (q) params.q = q;
      const { data } = await api.get("/assessments/subjects", { params });
      setSubjects(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadGroups = async () => {
    try { const { data } = await api.get("/assessments/class-groups"); setGroups(data); } catch (e) { /* silent */ }
  };
  const loadPickers = async () => {
    try {
      const [pRes, sRes, seRes] = await Promise.all([
        api.get("/programs", { params: { active_only: true } }),
        api.get("/staff"),
        api.get("/academic-sessions"),
      ]);
      const enriched = await Promise.all(pRes.data.map(async (p) => {
        const cls = (await api.get("/classes", { params: { program_id: p.id } })).data;
        return { ...p, classes: cls };
      }));
      setPrograms(enriched);
      setStaff(sRes.data.filter((s) => (s.roles || [s.role] || []).includes("teacher")));
      setCurrentSession(seRes.data.find((s) => s.is_current) || seRes.data[0] || null);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadGroups(); loadPickers(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, group, showActive]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setOpenDlg(true); };
  const openEdit = (s) => { setForm({ name: s.name, code: s.code || "", class_group: s.class_group, is_active: s.is_active, description: s.description || "" }); setEditing(s); setOpenDlg(true); };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    try {
      if (editing) await api.patch(`/assessments/subjects/${editing.id}`, form);
      else await api.post("/assessments/subjects", form);
      toast.success(editing ? "Subject updated" : "Subject created");
      setOpenDlg(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (s) => {
    try { await api.patch(`/assessments/subjects/${s.id}`, { is_active: !s.is_active }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? Existing marks will be preserved but assignments will be removed.`)) return;
    try { await api.delete(`/assessments/subjects/${s.id}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openAssignments = async (s) => {
    setAsnSubject(s); setAsnOpen(true);
    try {
      if (!currentSession) return;
      const { data } = await api.get("/assessments/subject-assignments", { params: { subject_id: s.id, session_id: currentSession.id, term: asnTerm } });
      setAsnRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reloadAsn = async () => {
    if (!asnSubject || !currentSession) return;
    const { data } = await api.get("/assessments/subject-assignments", { params: { subject_id: asnSubject.id, session_id: currentSession.id, term: asnTerm } });
    setAsnRows(data);
  };
  useEffect(() => { reloadAsn(); /* eslint-disable-next-line */ }, [asnTerm]);

  const addAssignment = async (class_id, arm_id, teacher_ids) => {
    if (!currentSession) return toast.error("No academic session set");
    try {
      await api.post("/assessments/subject-assignments", {
        subject_id: asnSubject.id, class_id, arm_id: arm_id || undefined, teacher_ids,
        session_id: currentSession.id, term: asnTerm,
      });
      toast.success("Assignment saved"); await reloadAsn();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeAssignment = async (id) => {
    if (!window.confirm("Remove this assignment?")) return;
    try { await api.delete(`/assessments/subject-assignments/${id}`); await reloadAsn(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="subjects-page">
      <PageHeader
        overline="Curriculum"
        title="Subjects"
        description="Create the subjects taught in each class group, then assign teachers per class and term."
        actions={
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subjects…" className="pl-9 h-10 w-64 bg-white" data-testid="subjects-search" />
            </div>
            {canManage && (
              <Button onClick={openCreate} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="add-subject-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Subject
              </Button>
            )}
          </>
        }
      />

      {/* Class group filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setGroup("all")} className={"text-xs px-3 py-1.5 rounded-full border transition " + (group === "all" ? "bg-[#1B2C5A] text-white border-[#1B2C5A]" : "bg-white text-[#1B2C5A] border-[#E2E8F0] hover:border-[#9DCD3F]")} data-testid="group-all">
          All Groups · {subjects.length}
        </button>
        {GROUPS_ORDER.map((code) => {
          const g = groups.find((x) => x.code === code);
          if (!g) return null;
          return (
            <button key={code} onClick={() => setGroup(code)} className={"text-xs px-3 py-1.5 rounded-full border transition " + (group === code ? GROUP_TONE[code] : "bg-white text-[#1B2C5A] border-[#E2E8F0] hover:border-[#9DCD3F]")} data-testid={`group-${code}`}>
              {g.label} <span className="text-[#9DCD3F] ml-1">· {g.class_count} class{g.class_count !== 1 ? "es" : ""}</span>
            </button>
          );
        })}
        <button onClick={() => setShowActive((v) => !v)} className={"text-xs px-3 py-1.5 rounded-full border transition ml-auto " + (showActive ? "bg-[#9DCD3F] text-[#1B2C5A] border-[#9DCD3F]" : "bg-white text-[#475569] border-[#E2E8F0]")} data-testid="filter-active">
          Active only
        </button>
      </div>

      {subjects.length === 0 ? (
        <EmptyState message={q ? "No subjects match your search." : (canManage ? "No subjects yet. Click New Subject to add the first one." : "No subjects have been created yet.")} testId="subjects-empty" />
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead>Subject</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Class Group</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subjects.map((s) => (
                <TableRow key={s.id} data-testid={`subject-row-${s.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="w-8 h-8 rounded-lg bg-[#9DCD3F]/15 text-[#1B2C5A] grid place-items-center"><BookOpen className="w-4 h-4" /></span>
                      <div>
                        <div className="font-medium text-[#1B2C5A]">{s.name}</div>
                        {s.description && <div className="text-xs text-[#475569] mt-0.5 truncate max-w-md">{s.description}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{s.code || "—"}</TableCell>
                  <TableCell>
                    <span className={"text-xs px-2 py-0.5 rounded-full border " + GROUP_TONE[s.class_group]}>
                      {groups.find((g) => g.code === s.class_group)?.label || s.class_group}
                    </span>
                  </TableCell>
                  <TableCell>
                    {s.is_active
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Inactive</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openAssignments(s)} className="text-[#1B2C5A]" data-testid={`assignments-${s.id}`} title="Classes & Teachers">
                        <Users2 className="w-4 h-4" />
                      </Button>
                      {canManage && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => toggleActive(s)} className={s.is_active ? "text-slate-600" : "text-emerald-700"} data-testid={`toggle-${s.id}`} title={s.is_active ? "Deactivate" : "Activate"}>
                            {s.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="text-[#1B2C5A]" data-testid={`edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(s)} className="text-red-700" data-testid={`del-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#9DCD3F]" /> {editing ? "Edit Subject" : "New Subject"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-[#1B2C5A]">Subject Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="subject-name" placeholder="e.g., Mathematics, English Language, Qur'anic Studies" /></div>
            <div><Label className="text-xs text-[#1B2C5A]">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MATH" /></div>
            <div>
              <Label className="text-xs text-[#1B2C5A]">Class Group *</Label>
              <Select value={form.class_group} onValueChange={(v) => setForm({ ...form, class_group: v })}>
                <SelectTrigger data-testid="subject-group"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GROUPS_ORDER.map((code) => (
                    <SelectItem key={code} value={code}>{groups.find((g) => g.code === code)?.label || code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs text-[#1B2C5A]">Description (optional)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDlg(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="subject-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignments dialog */}
      <Dialog open={asnOpen} onOpenChange={setAsnOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Users2 className="w-4 h-4 text-[#9DCD3F]" /> {asnSubject?.name} — Class & Teacher Assignments
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 -mt-2 mb-2">
            <div className="text-xs text-[#475569]">Term:</div>
            <Select value={asnTerm} onValueChange={setAsnTerm}>
              <SelectTrigger className="h-8 w-40" data-testid="asn-term"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First Term</SelectItem>
                <SelectItem value="second">Second Term</SelectItem>
                <SelectItem value="third">Third Term</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-[#475569] ml-auto">{currentSession?.name || "—"}</div>
          </div>

          {asnRows.length === 0 ? (
            <p className="text-sm text-[#475569] italic">No classes assigned yet. Add one below.</p>
          ) : (
            <div className="space-y-2 mb-3">
              {asnRows.map((a) => (
                <div key={a.id} className="rounded-lg border border-[#E2E8F0] p-3 flex items-center gap-3" data-testid={`asn-${a.id}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1B2C5A]">
                      {a.class_name}{a.arm_name ? ` · ${a.arm_name}` : " · All Arms"}
                    </div>
                    <div className="text-xs text-[#475569] mt-0.5">
                      {a.teachers?.length ? a.teachers.map((t) => t.name).join(", ") : <span className="italic">No teachers assigned</span>}
                    </div>
                  </div>
                  {canManage && (
                    <Button size="sm" variant="ghost" onClick={() => removeAssignment(a.id)} className="text-red-700" data-testid={`asn-remove-${a.id}`}><XIcon className="w-4 h-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {canManage && (
            <AsnAddRow programs={programs} staff={staff} onAdd={addAssignment} />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAsnOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AsnAddRow = ({ programs, staff, onAdd }) => {
  const [programId, setProgramId] = useState("");
  const [classId, setClassId] = useState("");
  const [armId, setArmId] = useState("");
  const [teacherIds, setTeacherIds] = useState([]);
  const prog = programs.find((p) => p.id === programId);
  const cls = (prog?.classes || []).find((c) => c.id === classId);
  const arms = cls?.arms || [];

  const submit = () => {
    if (!classId) return;
    onAdd(classId, armId || null, teacherIds);
    setProgramId(""); setClassId(""); setArmId(""); setTeacherIds([]);
  };

  return (
    <div className="rounded-lg border border-dashed border-[#9DCD3F] bg-[#9DCD3F]/8 p-3 space-y-3">
      <div className="text-xs uppercase tracking-wider text-[#9DCD3F] font-medium">Add Class Assignment</div>
      <div className="grid grid-cols-3 gap-2">
        <Select value={programId} onValueChange={(v) => { setProgramId(v); setClassId(""); setArmId(""); }}>
          <SelectTrigger className="h-9 bg-white" data-testid="asn-program"><SelectValue placeholder="Program" /></SelectTrigger>
          <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={classId} onValueChange={(v) => { setClassId(v); setArmId(""); }} disabled={!prog}>
          <SelectTrigger className="h-9 bg-white" data-testid="asn-class"><SelectValue placeholder="Class" /></SelectTrigger>
          <SelectContent>{(prog?.classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={armId} onValueChange={setArmId} disabled={!arms.length}>
          <SelectTrigger className="h-9 bg-white" data-testid="asn-arm"><SelectValue placeholder="All Arms" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Arms</SelectItem>
            {arms.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-[#1B2C5A]">Teachers (multi-select — hold Ctrl/Cmd)</Label>
        <select multiple value={teacherIds} onChange={(e) => setTeacherIds(Array.from(e.target.selectedOptions, (o) => o.value))} className="w-full mt-1 rounded-md border border-[#E2E8F0] bg-white px-2 py-1.5 text-sm h-24" data-testid="asn-teachers">
          {staff.map((t) => <option key={t.id} value={t.id}>{t.full_name} — {t.designation || t.role}</option>)}
        </select>
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={!classId} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="asn-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Assignment</Button>
      </div>
    </div>
  );
};

export default Subjects;
